import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, GripHorizontal, X } from 'lucide-react'
import { useMediaQuery } from '@mui/material'
import { BoxTruckDriverLastLocation, CanonicalLoad } from '@numeo/types-core'
import { BoxTruckLoadAnnotation, BoxTruckOperatorBid, LoadBid } from '@numeo/types'
import { BoxTruckEmailThread, BoxTruckVerification, NearbyBoxTruckDriver } from '../services/boxTruckLoadsApi'
import { buildLoadClipboardText, numeoId, sourceIconKey, sourceLabel, vehicleLabel } from '../utils/loadFields'
import { SOURCE_ICONS } from 'features/load-hub/components/filters/SourcesSelect'
import { NearbyDriversTab } from './NearbyDriversTab'
import { DraftEmailTab } from './DraftEmailTab'
import { BoxTruckDriverSmsPane } from './BoxTruckDriverSmsPane'
import { CopyButton } from './BoxTruckDrawerActions'
import { LoadDetails } from './BoxTruckLoadDetails'
import { BoxTruckPricingPanel } from './BoxTruckPricingPanel'
import { useBoxTruckPricing } from '../hooks/useBoxTruckPricing'
import { type BoxTruckSmsChatEntry, trackBoxTruckUiInteraction } from '../utils/analytics'
import { useSelectedDriverRoadDistance } from '../hooks/useSelectedDriverRoadDistance'
import { buildDriverTemplateVariables } from '../utils/driverTemplateVariables'
import { resolveMilesOut } from '../utils/roadDistance'

interface BoxTruckLoadDrawerProps {
    load: CanonicalLoad | null
    /** The load has dropped off the live board (paginated off page 1 OR booked/expired/removed — indistinguishable
     *  client-side) and we're showing the last-known snapshot. Surfaces a "re-verify" banner. */
    stale?: boolean
    onClose: () => void
    /** Driver/carrier bids on this load (best-price-first). Shown in the load-detail column. */
    bids?: LoadBid[]
    /** Operators who have already sent a bid email to this load's broker. */
    operatorBids?: BoxTruckOperatorBid[]
    /** The team's shared rating + note on this load (null when none yet). */
    annotation?: BoxTruckLoadAnnotation | null
    /** Persist the team's rating + note (full state each call). `kind` picks the success toast copy. */
    onSetAnnotation?: (load: CanonicalLoad, rating: number | null, comment: string | null, kind: 'rating' | 'note') => void
    currentOperatorId?: string
    /** The board tab's picked "Search for driver" driver — Nearby Drivers pins them to the top of its
     *  list, synthesizing their row from this verification when the nearby fetch can't supply them. */
    pinnedDrivers?: BoxTruckVerification[]
    /** This load's broker thread as the board already resolved it, so the email workspace opens on it
     *  instead of re-asking the same endpoint for one load. */
    emailThread?: BoxTruckEmailThread | null
    /** A location the operator corrected from the Nearby Drivers list — the host patches the persisted
     *  selection `pinnedDrivers` comes from, so a later refetch can't rebuild the row from the old fix. */
    onDriverLocationSaved?: (userId: string, lastLocation: BoxTruckDriverLastLocation) => void
    /** Viewport y the overlay starts at, in px — the bottom edge of the host page's tab strip, so the strip
     *  stays visible AND clickable while a load is open. Hosts with nothing above the drawer pass nothing. */
    topOffset?: number
    /** Passed through to the detail panel's route block — see BoxTruckBoardPreferences.openMapOnExpand. */
    openMapOnExpand?: boolean
}

export const BoxTruckLoadDrawer = ({
    load,
    stale = false,
    onClose,
    bids = [],
    operatorBids = [],
    annotation = null,
    onSetAnnotation,
    currentOperatorId,
    pinnedDrivers,
    emailThread,
    onDriverLocationSaved,
    topOffset = 0,
    openMapOnExpand,
}: BoxTruckLoadDrawerProps) => {
    const isMobile = useMediaQuery('(max-width: 899px)')
    // Asked once, here, because two consumers need the same answer: the panel shows it and the bid email
    // records what the dispatcher did with it. Two hook calls would be two requests for one load.
    const pricing = useBoxTruckPricing(load)
    // Desktop layout: LOAD DETAIL (fixed left column) | right stack = NEARBY DRIVERS full-width on top,
    // EMAIL/BID workspace below. Mobile turns those regions into one vertical, page-scrolling flow.
    const { selectedDriver, selectedState, statesByDriverId, actions } = useSelectedDriverRoadDistance(load)
    /**
     * Whose SMS conversation is open beside the email, if any.
     *
     * Its OWN driver, not the selected one: the checkbox picks whose unit the offer email quotes, and
     * texting someone must not rewrite the email being composed for somebody else. Opt-in too —
     * nothing is read until a row's SMS button is pressed, so scanning the list costs no requests.
     *
     * Typed by what the pane actually needs rather than by the nearby-driver row, because a BID opens it
     * too (ADR-057): the bid names its driver, and that is the whole address the thread is resolved from.
     */
    /** Which button opened the SMS pane travels with the selection: the pane reports it with the open it
     *  already fires, so the bid-row button's adoption is answerable without a second event. */
    const [smsDriver, setSmsDriver] = useState<{ driverId: string; name: string | null; entry: BoxTruckSmsChatEntry } | null>(null)
    // Preserve the existing nearest-driver fallback for non-mileage email fields. Operational miles remain
    // selection-only: an unpicked nearest driver is a detail fallback, not the unit being quoted.
    const [nearbyDrivers, setNearbyDrivers] = useState<NearbyBoxTruckDriver[]>([])
    // False when the tab cannot promise its first row is the nearest truck (see `onDriversChange`).
    const [nearestKnown, setNearestKnown] = useState(true)
    const loadKey = `${load?.externalId ?? load?.id ?? ''}|${load?.pickupLat ?? ''}|${load?.pickupLon ?? ''}`
    useEffect(() => {
        setNearbyDrivers([])
        setNearestKnown(true)
        // The drawer is mounted once for the page and only renders null when closed, so anything not
        // reset here survives into the next load: X's conversation would reappear beside load B's
        // email, counted as another open and marking X's thread read again.
        setSmsDriver(null)
    }, [loadKey])
    // Selection is retained by id while the list refetches. Resolve the current row so fresh location/unit
    // data replaces the selection snapshot instead of leaking stale details into Copy Load or an email.
    const currentSelectedDriver = useMemo(
        () => (selectedDriver ? (nearbyDrivers.find((driver) => driver.driverId === selectedDriver.driverId) ?? selectedDriver) : null),
        [nearbyDrivers, selectedDriver]
    )
    // Copy must stay usable whatever the route is doing. Until the authoritative route arrives — and if it
    // never does — use the same direct estimate the selected row and the Offer email show. Unmarked:
    // copied text gets pasted to brokers, so the operator's ≈ cue stays in the table and nowhere else.
    const selectedCopyDistance = currentSelectedDriver
        ? resolveMilesOut(currentSelectedDriver.lastSeenAt, currentSelectedDriver.proximityDistanceMi, selectedState)
        : { milesOut: null, milesOutApproximate: false }
    const handleDriversChange = useCallback(
        (drivers: NearbyBoxTruckDriver[], isNearestKnown: boolean) => {
            setNearbyDrivers(drivers)
            setNearestKnown(isNearestKnown)
            const firstDriver = drivers[0]
            if (firstDriver && bids.some((bid) => bid.bidderId === firstDriver.driverId)) actions.selectInitialDriver(firstDriver)
            if (!selectedDriver) return
            const refreshedSelection = drivers.find((driver) => driver.driverId === selectedDriver.driverId)
            if (refreshedSelection && refreshedSelection.lastSeenAt !== selectedDriver.lastSeenAt) actions.driverLocationChanged(refreshedSelection)
        },
        [actions, bids, selectedDriver]
    )

    // Preserve the nearest-driver fallback for unit details; `miles_out` remains selection-only and falls
    // back to the estimate whenever the picked driver's road route isn't current. No fallback when the nearest
    // is unknown: the first row is then some other truck, and the broker would get its unit as if it were ours.
    const driverVariables = useMemo(
        () => buildDriverTemplateVariables(currentSelectedDriver, nearestKnown ? nearbyDrivers[0] : undefined, selectedState),
        [currentSelectedDriver, selectedState, nearbyDrivers, nearestKnown]
    )

    // The checked driver's own bid on this load (bidderId ↔ driverId) — seeds the calculator's Price for
    // Driver so the cost side starts at what the driver asked. Null when unpicked / they haven't bid.
    const selectedDriverBid = useMemo(() => {
        if (!currentSelectedDriver) return null
        return bids.find((b) => b.bidderId === currentSelectedDriver.driverId)?.price ?? null
    }, [bids, currentSelectedDriver])

    // User-draggable height of the bottom email/bid pane (the grip divider). Clamped so neither pane can
    // be dragged away entirely; drivers take whatever is left above.
    const [emailPaneHeight, setEmailPaneHeight] = useState(() => Math.round(window.innerHeight * 0.45))
    const stackRef = useRef<HTMLDivElement | null>(null)
    // Live drag teardown — also invoked on unmount, so closing the drawer mid-drag (Escape stays bound
    // while dragging) can't leak the window listeners.
    const dragCleanupRef = useRef<(() => void) | null>(null)
    useEffect(() => () => dragCleanupRef.current?.(), [])
    const startPaneDrag = (e: React.PointerEvent) => {
        e.preventDefault()
        const startY = e.clientY
        const startH = emailPaneHeight
        const max = Math.max(200, (stackRef.current?.clientHeight ?? window.innerHeight) - 140)
        const onMove = (ev: PointerEvent) => setEmailPaneHeight(Math.min(max, Math.max(150, startH + (startY - ev.clientY))))
        const onUp = () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
            window.removeEventListener('pointercancel', onUp)
            dragCleanupRef.current = null
        }
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
        // pointercancel: touch/pen gestures the browser interrupts (tab switch, system gesture) end the drag
        // without a pointerup — without this the pane would stay glued to the cursor.
        window.addEventListener('pointercancel', onUp)
        dragCleanupRef.current = onUp
    }

    useEffect(() => {
        if (!load) return
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [load, onClose])

    if (!load) return null

    const copyLoadValue = () => {
        if (!currentSelectedDriver) return buildLoadClipboardText(load)
        return buildLoadClipboardText(load, selectedCopyDistance)
    }

    const nearbyDriversPanel = (
        <NearbyDriversTab
            load={load}
            bids={bids}
            pinnedDrivers={pinnedDrivers}
            onDriverLocationSaved={onDriverLocationSaved}
            selectedDriverId={selectedDriver?.driverId ?? null}
            onSelectDriver={actions.selectDriver}
            // Mobile has no second column to put the panel in, and the prop's contract is that a host
            // without one does not offer the button — a tap that highlights a row and opens nothing
            // reads as a broken feature.
            onOpenSms={isMobile ? undefined : (driver) => setSmsDriver({ ...driver, entry: 'nearby' })}
            smsDriverId={smsDriver?.driverId ?? null}
            roadDistanceStates={statesByDriverId}
            onDriverLocationChanged={actions.driverLocationChanged}
            onDriversChange={handleDriversChange}
            headerExtra={
                isMobile ? undefined : (
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                    >
                        <X size={18} />
                    </button>
                )
            }
        />
    )
    const emailWorkspace = (
        <DraftEmailTab
            key={load.externalId || load.id}
            load={load}
            onActionComplete={onClose}
            selectedDriverKey={selectedDriver?.driverId ?? ''}
            // `currentSelectedDriver`, so the name recorded is the refreshed row's rather than the
            // selection snapshot's — the same reason Copy Load and the email body use it.
            bidDriver={currentSelectedDriver ? { id: currentSelectedDriver.driverId, name: currentSelectedDriver.name } : null}
            driverBidPrice={selectedDriverBid}
            driverVariables={driverVariables}
            seedThread={emailThread}
            pricingRequest={pricing.status === 'ready' ? pricing.request : null}
            pricingShown={pricing.status === 'ready'}
        />
    )

    return (
        <>
            <div className="fixed inset-x-0 bottom-0 z-[9996] bg-black/40" style={{ top: topOffset }} onClick={onClose} aria-hidden />
            {/* Both layers start at `topOffset` — the loadboard passes its tab strip's bottom edge so the
                operator can switch search tabs with the drawer open; a full-viewport backdrop swallowed those
                clicks even where the strip was visible. It is a PROP, not a global CSS variable: this drawer is
                also mounted app-wide by BoxTruckBidNotifications, which would otherwise inherit the loadboard's
                offset while the operator happens to be standing on that page. Other hosts omit it → 0 = full. */}
            <aside
                className="fixed bottom-0 right-0 z-[9997] flex w-full max-w-none flex-col border-l border-border bg-surface shadow-xl min-[900px]:w-[98vw] min-[900px]:max-w-[1820px] min-[1280px]:w-[85vw]"
                style={{ top: topOffset }}
            >
                {/* Stale banner: the load dropped off the live board (may be booked/expired), so warn before the
                    operator bids/contacts on possibly-outdated data. Actions stay enabled — a still-live load that
                    was merely paginated off page 1 must remain biddable. */}
                {stale && (
                    <div className="flex items-center gap-2 border-b border-warning bg-warning-soft px-5 py-2.5 text-xs font-medium text-warning-soft-foreground">
                        <AlertTriangle size={15} strokeWidth={2} className="shrink-0" />
                        <span>This load has dropped off the live board and may be booked or expired — re-verify with the broker before bidding.</span>
                    </div>
                )}

                {/* Every scroller in here is `overscroll-contain`: without it, reaching the end of the Nearby
                    Drivers list hands the rest of the wheel gesture to the loadboard BEHIND the drawer, which
                    then scrolls under the overlay the operator is reading.
                    Body: LOAD DETAIL (fixed left column) | right stack. The right side mirrors atrek's dispatch
                    modal: the NEARBY DRIVERS table gets the FULL remaining width on top (it's what the operator
                    scans and copies from), and the email/bid workspace (calculator + compose side by side) sits
                    BELOW it — replacing the old three side-by-side columns that squeezed the drivers table. */}
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain min-[900px]:flex-row min-[900px]:overflow-hidden">
                    {/* Column 1 — load detail (fixed width, own scroll). The former top navbar's bits — load id,
                        source, vehicle + map/copy actions — now lead this column, so no full header row is needed. */}
                    <div className="min-h-0 shrink-0 grow-0 basis-auto overscroll-contain border-b border-border px-3 py-4 min-[900px]:basis-[340px] min-[900px]:overflow-y-auto min-[900px]:border-b-0 min-[900px]:border-r min-[900px]:px-5">
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                            {numeoId(load) && <span className="text-base font-mono font-semibold text-foreground">{numeoId(load)}</span>}
                            <span className="inline-flex items-center" title={sourceLabel(load)}>
                                {SOURCE_ICONS[sourceIconKey(load)] ?? (
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-default-foreground">{sourceLabel(load)}</span>
                                )}
                            </span>
                            <span className="inline-flex items-center rounded bg-default px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-default-foreground">
                                {vehicleLabel(load)}
                            </span>
                            {/* Copy a clean plaintext summary of the load (route, schedule, specs, notes) + the explicitly
                                picked driver's unit block, so a copied load is offer-ready. Rides the id row,
                                pushed to its right edge. */}
                            <CopyButton
                                value={copyLoadValue}
                                label="Copy load"
                                onCopied={() => trackBoxTruckUiInteraction('copy_load')}
                                className="ml-auto hidden items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-default min-[900px]:inline-flex"
                            >
                                {(copied) => (copied ? 'Copied' : 'Copy Load')}
                            </CopyButton>
                            <div className="flex w-full items-center justify-between border-t border-border pt-2 min-[900px]:hidden">
                                <CopyButton
                                    value={copyLoadValue}
                                    label="Copy load"
                                    onCopied={() => trackBoxTruckUiInteraction('copy_load')}
                                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-default"
                                >
                                    {(copied) => (copied ? 'Copied' : 'Copy Load')}
                                </CopyButton>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label="Close"
                                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                        <LoadDetails
                            load={load}
                            bids={bids}
                            operatorBids={operatorBids}
                            annotation={annotation}
                            onSetAnnotation={onSetAnnotation}
                            currentOperatorId={currentOperatorId}
                            openMapOnExpand={openMapOnExpand}
                            // The driver who bid is one press from their conversation — on the desktop
                            // layout, which is where the pane has a column to live in.
                            onTextBidder={isMobile ? undefined : (bid) => setSmsDriver({ driverId: bid.bidderId, name: bid.bidderName ?? null, entry: 'bid' })}
                            afterPrimaryDetails={
                                isMobile ? (
                                    <>
                                        <div className="mt-4 max-h-[55dvh] overflow-y-auto overscroll-contain border-y border-border py-3">
                                            {nearbyDriversPanel}
                                        </div>
                                        <section className="flex min-h-[70dvh] flex-col gap-3 py-3">
                                            <BoxTruckPricingPanel state={pricing} load={load} />
                                            {emailWorkspace}
                                        </section>
                                    </>
                                ) : undefined
                            }
                        />
                    </div>

                    {/* Desktop keeps the original grip-resizable drivers/email split. Mobile injects both panels
                        between primary and secondary LoadDetails in the requested reading order. */}
                    {!isMobile && (
                        <div ref={stackRef} className="flex min-h-0 min-w-0 flex-1 flex-col">
                            {/* Top — nearby drivers at full width (own scroll; renders its own header + radius); takes
                                whatever height the dragged email pane leaves. The drawer's Close ✕ slots into its
                                header row (headerExtra), the drawer's true top-right corner. */}
                            <div className="flex min-h-0 flex-1 basis-0 flex-col overflow-y-auto overscroll-contain px-3 pb-2 pt-3">{nearbyDriversPanel}</div>

                            {/* Grip divider — drag up/down to trade height between the drivers table and the email
                                pane. The whole strip is the hit area. */}
                            <div
                                role="separator"
                                aria-orientation="horizontal"
                                aria-label="Resize email pane"
                                onPointerDown={startPaneDrag}
                                className="flex shrink-0 cursor-row-resize items-center justify-center border-y border-border bg-background py-0.5 text-foreground-muted/60 transition-colors hover:bg-default hover:text-foreground-muted"
                            >
                                <GripHorizontal size={16} />
                            </div>

                            {/* Bottom — email/bid workspace at the dragged height. Wide + short, so the calculator
                                sits BESIDE the compose (stacked=false); flex column + min-h-0 keeps the compose
                                scrolling internally. */}
                            {/* Pricing sits at the head of this row, immediately left of the bid calculator — the
                                quote is what the dispatcher then types into it, so the two belong side by side.
                                The panel carries its own width and scrolling, so when the Gluon tick is off it
                                renders nothing and leaves no empty column; the compose takes the space back. */}
                            <section className="flex min-h-0 shrink-0 gap-3 px-5 pb-3 pt-2" style={{ height: emailPaneHeight }}>
                                <BoxTruckPricingPanel state={pricing} load={load} className="w-[290px] shrink-0 overflow-y-auto overscroll-contain" />
                                <div className="flex min-h-0 min-w-0 flex-1 flex-col">{emailWorkspace}</div>
                                {/* SMS beside the email, each taking half of what is left — the two halves of
                                    the same conversation with the driver who should take this load. Opened
                                    from that driver's own row, so the email keeps the full width until one is
                                    asked for, and `key` remounts it when the operator moves to another. */}
                                {smsDriver && (
                                    <BoxTruckDriverSmsPane
                                        key={smsDriver.driverId}
                                        driverAccountId={smsDriver.driverId}
                                        driverName={smsDriver.name}
                                        entry={smsDriver.entry}
                                        onClose={() => setSmsDriver(null)}
                                    />
                                )}
                            </section>
                        </div>
                    )}
                </div>
            </aside>
        </>
    )
}
